<div class="flex min-w-0 flex-col gap-1.5">
  @if (sourceOptions().length > 1) {
    <div
      class="gx-segmented is-sm self-start"
      role="radiogroup"
      [attr.aria-label]="'features.data.path_filter.value.source' | translate">
      @for (option of sourceOptions(); track option.id) {
        <button
          type="button"
          role="radio"
          class="seg"
          [class.is-active]="source() === option.id"
          [attr.aria-checked]="source() === option.id"
          (click)="onSource(option.id)">
          @if (option.id === 'parameter') {
            <i [class]="ICONS.bracketsCurly" aria-hidden="true"></i>
          }
          {{ option.label }}
        </button>
      }
    </div>
  }

  @if (editor().kind === 'label') {
    <app-select
      [items]="labelKeyItems()"
      [searchable]="labelKeyItems().length > 8"
      [value]="labelArg().label || null"
      [placeholder]="'features.data.path_filter.value.label_key' | translate"
      (valueChange)="onLabelKey($event)" />
  }

  @switch (source()) {
    @case ('parameter') {
      @if (parameterName(); as name) {
        <span class="gx-token self-start">
          <i [class]="ICONS.bracketsCurly" aria-hidden="true"></i>{{ name }}
          <button
            type="button"
            class="hover:bg-accent-interactive-hover inline-flex cursor-pointer rounded-full p-0.5 transition-colors"
            [attr.aria-label]="'common.remove' | translate"
            (click)="clearSlot()">
            <i [class]="ICONS.x" aria-hidden="true" class="text-[10px]"></i>
          </button>
        </span>
      } @else {
        <app-select
          [items]="parameterItems()"
          [searchable]="parameterItems().length > 8"
          [placeholder]="'features.data.path_filter.value.pick_parameter' | translate"
          (valueChange)="onParameter($event)" />
        <button
          type="button"
          class="text-accent-main hover:bg-accent-interactive inline-flex cursor-pointer items-center gap-1.5 self-start rounded-xs px-2 py-1 text-xs font-semibold transition-colors"
          (click)="newParameter()">
          <i [class]="ICONS.plus" aria-hidden="true"></i
          >{{ 'features.data.path_filter.value.new_parameter' | translate }}
        </button>
      }
    }
    @case ('self_field') {
      @if (selfField(); as chosen) {
        <span class="label-pill self-start">
          <span class="k">{{ context().selfLabel }}</span
          ><span class="v">{{ chosen.label }}</span>
        </span>
      }
      <app-select
        [items]="selfFieldItems()"
        [value]="selfField()?.id ?? null"
        [searchable]="true"
        [placeholder]="'features.data.path_filter.value.pick_field' | translate"
        (valueChange)="onSelfField($event)" />
    }
    @case ('self_label') {
      <input
        type="text"
        class="form-input rounded-xs px-2.5 py-1.5 font-mono text-[12.5px] leading-5"
        [value]="selfLabelText()"
        [placeholder]="'features.data.path_filter.value.label_key' | translate"
        [attr.aria-label]="
          'features.data.path_filter.value.self_label'
            | translate: { record: context().selfLabel ?? '' }
        "
        (input)="onSelfLabel($any($event.target).value)" />
    }
    @default {
      @switch (editor().kind) {
        @case ('bool') {
          <div
            class="gx-segmented is-sm self-start"
            role="radiogroup"
            [attr.aria-label]="'features.data.path_filter.value.input' | translate">
            <button
              type="button"
              role="radio"
              class="seg"
              [class.is-active]="boolValue() === true"
              [attr.aria-checked]="boolValue() === true"
              (click)="onBool(true)">
              {{ 'features.data.path_filter.value.yes' | translate }}
            </button>
            <button
              type="button"
              role="radio"
              class="seg"
              [class.is-active]="boolValue() === false"
              [attr.aria-checked]="boolValue() === false"
              (click)="onBool(false)">
              {{ 'features.data.path_filter.value.no' | translate }}
            </button>
          </div>
        }
        @case ('enum') {
          @if (layout() === 'stacked' && editor().multiple) {
            <div
              class="g-menu-scroll -mx-1 flex flex-col"
              role="group"
              [attr.aria-label]="'features.data.path_filter.value.input' | translate">
              @for (item of enumItems(); track item.id) {
                @let checked = selectedValues().includes(item.id);
                <button
                  type="button"
                  role="menuitemcheckbox"
                  class="g-menu-item w-full"
                  [attr.aria-checked]="checked"
                  (click)="toggleChoice(item.id)">
                  <span
                    class="inline-flex size-[15px] shrink-0 items-center justify-center rounded-[3px] border-[1.5px] text-[10px] transition-colors"
                    [class]="
                      checked
                        ? 'bg-accent-main border-accent-main text-text-on-accent'
                        : 'border-strong text-transparent'
                    ">
                    <i [class]="ICONS.check" aria-hidden="true"></i>
                  </span>
                  <span class="ellipsis flex-1 text-left text-[12.5px]">{{
                    item.label
                  }}</span>
                </button>
              }
            </div>
          } @else {
            <app-select
              [items]="enumItems()"
              [multiple]="editor().multiple"
              [searchable]="editor().values.length > 8"
              [value]="
                editor().multiple ? selectedValues() : (selectedValues().at(0) ?? null)
              "
              [placeholder]="'features.data.path_filter.value.input' | translate"
              (valueChange)="onSelect($event)" />
          }
        }
        @case ('reference') {
          <app-entity-auto-complete
            [value]="
              editor().multiple ? selectedValues() : (selectedValues().at(0) ?? null)
            "
            [entityType]="editor().entityType ?? ''"
            [isArray]="editor().multiple"
            (valueChange)="onReference($event)" />
        }
        @case ('scalar') {
          @if (editsAsDuration('value', $any(arg()))) {
            <app-duration-input
              [value]="durationOf($any(arg()))"
              [ariaLabel]="'features.data.path_filter.value.input' | translate"
              (valueChange)="onDuration('value', $event)" />
          } @else {
            <input
              class="form-input rounded-xs px-2.5 py-1.5 font-mono text-[12.5px] leading-5"
              [class.has-error]="rejectedText('value') !== undefined"
              [attr.aria-invalid]="rejectedText('value') !== undefined"
              [type]="inputType()"
              [value]="rejectedText('value') ?? scalarText()"
              [placeholder]="hint()"
              [attr.aria-label]="'features.data.path_filter.value.input' | translate"
              (change)="onScalar($any($event.target).value)" />
          }
        }
        @case ('range') {
          <div class="grid grid-cols-2 gap-1.5">
            @for (end of ['lo', 'hi']; track end) {
              @let bound = end === 'lo' ? range().lo : range().hi;
              @let label =
                end === 'lo'
                  ? 'features.data.path_filter.value.from'
                  : 'features.data.path_filter.value.to';
              @if (editsAsDuration($any(end), bound)) {
                <app-duration-input
                  [value]="durationOf(bound)"
                  [ariaLabel]="label | translate"
                  (valueChange)="onDuration($any(end), $event)" />
              } @else {
                <input
                  class="form-input rounded-xs px-2.5 py-1.5 font-mono text-[12.5px] leading-5"
                  [class.has-error]="rejectedText($any(end)) !== undefined"
                  [attr.aria-invalid]="rejectedText($any(end)) !== undefined"
                  [type]="inputType()"
                  [value]="rejectedText($any(end)) ?? scalarOf(bound)"
                  [placeholder]="hint()"
                  [attr.aria-label]="label | translate"
                  (change)="onRange($any(end), $any($event.target).value)" />
              }
            }
          </div>
        }
        @case ('list') {
          <input
            type="text"
            class="form-input rounded-xs px-2.5 py-1.5 font-mono text-[12.5px] leading-5"
            [class.has-error]="rejectedText('list') !== undefined"
            [attr.aria-invalid]="rejectedText('list') !== undefined"
            [value]="rejectedText('list') ?? listText()"
            [placeholder]="'features.data.path_filter.value.list_hint' | translate"
            [attr.aria-label]="'features.data.path_filter.value.input' | translate"
            (change)="onList($any($event.target).value)" />
        }
        @case ('relative_date') {
          <app-select
            [items]="relativeItems()"
            [value]="relativeValue()"
            [placeholder]="'features.data.path_filter.value.input' | translate"
            (valueChange)="onRelative($event)" />
        }
        @case ('text') {
          <input
            type="text"
            class="form-input rounded-xs px-2.5 py-1.5 font-mono text-[12.5px] leading-5"
            [value]="textValue()"
            [attr.aria-label]="'features.data.path_filter.value.input' | translate"
            (input)="onText($any($event.target).value)" />
        }
        @case ('texts') {
          <input
            type="text"
            class="form-input rounded-xs px-2.5 py-1.5 font-mono text-[12.5px] leading-5"
            [value]="textsValue()"
            [placeholder]="'features.data.path_filter.value.list_hint' | translate"
            [attr.aria-label]="'features.data.path_filter.value.input' | translate"
            (input)="onTexts($any($event.target).value)" />
        }
        @case ('label') {
          @if (editor().labelShape === 'label_range') {
            <div class="grid grid-cols-2 gap-1.5">
              <input
                type="text"
                class="form-input rounded-xs px-2.5 py-1.5 font-mono text-[12.5px] leading-5"
                [class.has-error]="rejectedText('label_lo') !== undefined"
                [attr.aria-invalid]="rejectedText('label_lo') !== undefined"
                [value]="rejectedText('label_lo') ?? scalarOf(labelArg().lo)"
                [attr.aria-label]="'features.data.path_filter.value.from' | translate"
                (change)="onLabelRange('lo', $any($event.target).value)" />
              <input
                type="text"
                class="form-input rounded-xs px-2.5 py-1.5 font-mono text-[12.5px] leading-5"
                [class.has-error]="rejectedText('label_hi') !== undefined"
                [attr.aria-invalid]="rejectedText('label_hi') !== undefined"
                [value]="rejectedText('label_hi') ?? scalarOf(labelArg().hi)"
                [attr.aria-label]="'features.data.path_filter.value.to' | translate"
                (change)="onLabelRange('hi', $any($event.target).value)" />
            </div>
          } @else if (labelValueControl() === 'enum') {
            <app-select
              [items]="labelVariantItems()"
              [multiple]="editor().labelShape === 'label_literals'"
              [searchable]="labelVariantItems().length > 8"
              [value]="
                editor().labelShape === 'label_literals'
                  ? labelValues()
                  : (labelValues().at(0) ?? null)
              "
              [placeholder]="'features.data.path_filter.value.input' | translate"
              (valueChange)="onLabelChoice($event)" />
          } @else if (labelValueControl() === 'reference') {
            <app-entity-auto-complete
              [value]="
                editor().labelShape === 'label_literals'
                  ? labelValues()
                  : (labelValues().at(0) ?? null)
              "
              [entityType]="labelReferenceType()"
              [isArray]="editor().labelShape === 'label_literals'"
              (valueChange)="onLabelChoice($event)" />
          } @else if (labelValueControl() === 'bool') {
            <div
              class="gx-segmented is-sm self-start"
              role="radiogroup"
              [attr.aria-label]="'features.data.path_filter.value.input' | translate">
              <button
                type="button"
                role="radio"
                class="seg"
                [class.is-active]="labelBool() === true"
                [attr.aria-checked]="labelBool() === true"
                (click)="onLabelChoice(true)">
                {{ 'features.data.path_filter.value.yes' | translate }}
              </button>
              <button
                type="button"
                role="radio"
                class="seg"
                [class.is-active]="labelBool() === false"
                [attr.aria-checked]="labelBool() === false"
                (click)="onLabelChoice(false)">
                {{ 'features.data.path_filter.value.no' | translate }}
              </button>
            </div>
          } @else {
            <input
              type="text"
              class="form-input rounded-xs px-2.5 py-1.5 font-mono text-[12.5px] leading-5"
              [class.has-error]="rejectedText('label_value') !== undefined"
              [attr.aria-invalid]="rejectedText('label_value') !== undefined"
              [value]="rejectedText('label_value') ?? labelValueText()"
              [placeholder]="
                editor().labelShape === 'label_literals'
                  ? ('features.data.path_filter.value.list_hint' | translate)
                  : ''
              "
              [attr.aria-label]="'features.data.path_filter.value.input' | translate"
              (change)="onLabelValue($any($event.target).value)" />
          }
        }
      }
      @if (hasRejection()) {
        <p class="form-help is-error mt-0" role="alert">
          {{ 'features.data.path_filter.value.invalid' | translate }}
        </p>
      }
    }
  }
</div>
